import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, Loader2, Search, BellRing, Download, RefreshCw, Trash2, History } from "lucide-react";
import { ConfirmButton } from "@/components/ConfirmButton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PageHeader } from "@/components/PageHeader";
import { ContractsTable } from "@/components/contracts/ContractsTable";
import { SyncStatusCard } from "@/components/contracts/SyncStatusCard";
import { EmployeeContractsSheet } from "@/components/contracts/EmployeeContractsSheet";
import { api, formatApiError } from "@/lib/api";

export default function Contracts() {
  const [data, setData] = useState({ rows: [], unmatched: [] });
  const [status, setStatus] = useState({ status: "never" });
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("tutti");
  const [selected, setSelected] = useState(null);
  const [starting, setStarting] = useState(false);

  const load = useCallback(() => {
    api.get("/contracts/overview").then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, []);
  const loadStatus = useCallback(() => api.get("/contracts/sync/status").then((r) => setStatus(r.data)).catch(() => {}), []);

  useEffect(() => { load(); loadStatus(); }, [load, loadStatus]);
  useEffect(() => {
    if (status.status !== "running") return;
    const t = setInterval(async () => { await loadStatus(); load(); }, 2500);
    return () => clearInterval(t);
  }, [status.status, load, loadStatus]);

  const startSync = async () => {
    setStarting(true);
    try {
      await api.post("/contracts/sync");
      toast.success("Lettura della casella avviata");
      setStatus({ status: "running", found: 0, imported: 0 });
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setStarting(false); }
  };

  const sendAlert = async () => {
    try {
      const { data } = await api.post("/contracts/expiry-alert/send-now");
      toast.success(`Avviso inviato via email a ${data.email}: ${data.count} contratti in scadenza`);
      if (data.errors?.length) toast.warning(data.errors.join("; "));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const startReparse = async () => {
    try {
      await api.post("/contracts/reparse");
      toast.success("Rianalisi di tutti i documenti avviata");
      setStatus({ status: "running", found: 0, imported: 0 });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const resetAll = async () => {
    try {
      const { data } = await api.delete("/contracts/all");
      toast.success(`Azzerati ${data.contracts_deleted} documenti e ${data.employees_deleted} dipendenti creati automaticamente`);
      setStatus({ status: "never" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const resetHistory = async () => {
    try {
      const { data } = await api.delete("/contracts/processed-emails");
      toast.success(`Cronologia azzerata (${data.deleted} email): la prossima lettura riesaminerà tutto il periodo`);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const deleteEmployee = async (r) => {
    try {
      const { data } = await api.delete(`/employees/${r.employee_id}`);
      toast.success(`${r.cognome} ${r.nome} eliminato con ${data.contracts_deleted} documenti`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const cancelSync = async () => {
    try { await api.post("/contracts/sync/cancel"); toast.success("Lettura annullata"); loadStatus(); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const exportXlsx = async () => {
    try {
      const res = await api.get("/contracts/export.xlsx", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = Object.assign(document.createElement("a"), { href: url, download: `contratti_${new Date().toISOString().slice(0, 10)}.xlsx` });
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const rows = data.rows.filter((r) => {
    if (filter === "senza" && r.stato !== "nessuno") return false;
    if (filter === "scaduti" && r.stato !== "scaduto") return false;
    if (filter === "in_scadenza" && r.stato !== "in_scadenza") return false;
    if (filter === "attivi" && r.stato !== "attivo") return false;
    const s = q.trim().toLowerCase();
    return !s || `${r.cognome} ${r.nome} ${r.codice_fiscale} ${r.current?.mansione || ""}`.toLowerCase().includes(s);
  });
  const counts = data.rows.reduce((a, r) => ({ ...a, [r.stato]: (a[r.stato] || 0) + 1 }), {});

  return (
    <>
      <PageHeader eyebrow="Documenti" title="Contratti"
        description="Contratti, proroghe e UNILAV letti dalla casella di posta e abbinati a ogni dipendente."
        actions={<div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={exportXlsx} className="active:scale-[0.98]" data-testid="export-contracts-button"><Download className="mr-2 h-4 w-4" /> Esporta Excel</Button>
          <Button variant="outline" onClick={startReparse} disabled={status.status === "running"} className="active:scale-[0.98]" data-testid="reparse-all-button"><RefreshCw className="mr-2 h-4 w-4" /> Rianalizza documenti</Button>
          <ConfirmButton testId="reset-contracts-button" variant="outline" title="Azzerare tutti i contratti?"
            description="Elimina tutti i documenti importati e i dipendenti creati automaticamente dai contratti (senza email). L'anagrafica inserita a mano resta. Poi potrai rileggere la casella."
            onConfirm={resetAll}><Trash2 className="mr-2 h-4 w-4" /> Azzera contratti</ConfirmButton>
          <ConfirmButton testId="reset-email-history-button" variant="outline" title="Rileggere tutte le email?"
            description="Normalmente vengono lette solo le email nuove. Azzerando la cronologia, la prossima lettura riesaminerà tutte le email del periodo configurato. I documenti già importati non vengono duplicati."
            onConfirm={resetHistory}><History className="mr-2 h-4 w-4" /> Rileggi tutte le email</ConfirmButton>
          {status.status === "running" && <Button variant="ghost" onClick={cancelSync} data-testid="cancel-sync-button">Annulla lettura</Button>}
          <Button variant="outline" onClick={sendAlert} className="active:scale-[0.98]" data-testid="send-expiry-alert-button"><BellRing className="mr-2 h-4 w-4" /> Invia avviso scadenze</Button>
          <Button onClick={startSync} disabled={starting || status.status === "running"} className="active:scale-[0.98]" data-testid="contracts-sync-button">
          {status.status === "running" || starting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Mail className="mr-2 h-4 w-4" />} Leggi dalla casella
        </Button></div>} />
      <SyncStatusCard status={status} unmatched={data.unmatched} onChanged={load} />
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative w-full sm:max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca dipendente o mansione" className="pl-9" data-testid="contracts-search-input" />
        </div>
        <Tabs value={filter} onValueChange={setFilter}>
          <TabsList className="flex-wrap">
            <TabsTrigger value="tutti" data-testid="contracts-filter-tutti">Tutti</TabsTrigger>
            <TabsTrigger value="attivi" data-testid="contracts-filter-attivi">Attivi {counts.attivo ? `(${counts.attivo})` : ""}</TabsTrigger>
            <TabsTrigger value="in_scadenza" data-testid="contracts-filter-in_scadenza">In scadenza {counts.in_scadenza ? `(${counts.in_scadenza})` : ""}</TabsTrigger>
            <TabsTrigger value="scaduti" data-testid="contracts-filter-scaduti">Scaduti {counts.scaduto ? `(${counts.scaduto})` : ""}</TabsTrigger>
            <TabsTrigger value="senza" data-testid="contracts-filter-senza">Senza contratto {counts.nessuno ? `(${counts.nessuno})` : ""}</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>
      <ContractsTable rows={rows} onOpen={setSelected} onDelete={deleteEmployee} />
      <EmployeeContractsSheet row={selected} onClose={() => setSelected(null)} onChanged={load} />
    </>
  );
}
